import { ReactComponent as DownloadIcon } from 'app/assets/img/download.svg';
import { StyledStack } from 'app/components/Stack';
import StatusComponent, { StatusState } from 'app/components/StatusComponent';
import StyledPagination from 'app/components/StyledPagination';
import StyledTable from 'app/components/StyledTable';
import useGetApiData from 'app/hooks/useGetApiData';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import TooltipParagraph from 'app/pages/Catalog/tooltipParagraph';
import { exportMyDownGetList } from 'app/services/checkTicketApi';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Layout } from '../layouts';
import { ACCOUNT_MNG_MENU } from '../layouts/AccountManager';
import FilesFilter from './_FilesFilter';
import MobileFilesFilter from './_MobileFilesFilter';
import { LoadingOutlined } from '@ant-design/icons';
import useTitleSize from 'app/components/StyledTable/useTitleSize';

const FilesManagement = () => {
  const from_date = moment().subtract(6, 'd').format('YYYY-MM-DD');
  const to_date = moment().format('YYYY-MM-DD');
  const defaultValue = useMemo(
    () => ({ from_date, to_date, limit: 20, page: 1 }),
    [from_date, to_date],
  );
  const translations = useSelector(selectTranslations);
  const [filter, setFilter] = useState(defaultValue);
  const [temporaryFilter, setTemporaryFilter] = useState(filter);
  const { data, isLoading, handleRequest } = useGetApiData({
    api: exportMyDownGetList,
    params: '',
    isSkip: true,
  });
  const handleDownload = (dataBase64: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = dataBase64;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const titleColumnSizes = useTitleSize({
    title: [
      'b2b.txt.agent-table-order',
      'b2b.file.file-name',
      'b2b.file.file-export-date',
      'b2b.file.classify',
      'b2b.file.export-status',
      'b2b.upgrade.action',
    ],
  });

  const columnTicket = [
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.txt.agent-table-order']}
        </div>
      ),
      key: 'order',
      render: (value, _, index) => {
        const itemOrder = (filter.page - 1) * filter.limit + index + 1;
        return (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.agent-table-order'],
            }}
          >
            {itemOrder}
          </div>
        );
      },
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.file.file-name']}
        </div>
      ),
      dataIndex: 'name',
      key: 'name',
      render: value => (
        <TooltipParagraph
          style={{
            width: Math.max(titleColumnSizes['b2b.file.file-name'], 250),
            textAlign: 'left',
            color: '#F8B117',
            marginBottom: 0,
          }}
          ellipsis={{ rows: 2 }}
        >
          {value}
        </TooltipParagraph>
      ),
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.file.file-export-date']}
        </div>
      ),
      dataIndex: 'created_at',
      key: 'created_at',
      render: value => {
        const [date, time] = value?.split(` `);
        return (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.file.file-export-date'],
            }}
          >
            <div>{date}</div>
            <div>{time}</div>
          </div>
        );
      },
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.file.classify']}
        </div>
      ),
      dataIndex: 'export_type',
      key: 'export_type',
      render: value => (
        <div
          style={{
            textAlign: 'left',
            minWidth: titleColumnSizes['b2b.file.classify'],
          }}
        >
          {value?.name}
        </div>
      ),
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.file.export-status']}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      render: status => {
        return (
          <div style={{ minWidth: titleColumnSizes['b2b.file.export-status'] }}>
            <StatusComponent label={status?.name} status={status?.value} />
          </div>
        );
      },
    },
    {
      title: (
        <div style={{ textAlign: 'center' }}>
          {translations?.['b2b.upgrade.action']}
        </div>
      ),
      key: 'href',
      render: value => {
        if (value?.status?.value === StatusState.FILE_SUCCESS) {
          return (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              style={{
                minWidth: titleColumnSizes['b2b.upgrade.action'],
                display: 'inline-block',
              }}
              onClick={() =>
                value?.exportData
                  ? handleDownload(value?.exportData, value?.name)
                  : null
              }
              href={value?.exportData ? undefined : value?.exportPath}
              download={value?.name}
            >
              <DownloadIcon style={{ cursor: 'pointer', color: '#F8B117' }} />
            </a>
          );
        }
        return (
          <div style={{ minWidth: titleColumnSizes['b2b.upgrade.action'] }}>
            <DownloadIcon
              style={{ cursor: 'no-drop', filter: 'contrast(0)' }}
            />
          </div>
        );
      },
    },
  ];

  const onRequestData = () => {
    setFilter({ ...temporaryFilter, page: 1 });
    handleRequest({ ...temporaryFilter, page: 1 });
  };

  useEffect(() => {
    handleRequest(defaultValue);
  }, [defaultValue, handleRequest]);

  const tableRef = useRef<any>();

  return (
    <Layout
      title={translations?.['b2b.file.title'] || ''}
      menuActive={ACCOUNT_MNG_MENU.FILES_MANAGEMENT}
    >
      <StyledStack direction={'column'} rowGap={'12px'}>
        <StyledStack display={{ lg: 'flex', md: 'none' }}>
          <FilesFilter setFilter={setTemporaryFilter} onClick={onRequestData} />
        </StyledStack>
        <StyledStack display={{ lg: 'none', md: 'flex' }}>
          <MobileFilesFilter
            setFilter={setTemporaryFilter}
            onClick={onRequestData}
          />
        </StyledStack>
        <StyledTable
          tableRef={tableRef}
          dataSource={data?.data}
          columns={columnTicket}
          pagination={false}
          emptyText={translations?.['b2b.file.no-result']}
          tableProps={{
            loading: {
              spinning: isLoading,
              indicator: (
                <LoadingOutlined style={{ fontSize: 24, opacity: 0.7 }} spin />
              ),
            },
            scroll: {
              x: data?.data?.length ? 'auto' : 'max-content',
              y: data?.data?.length ? '50vh' : undefined,
            },
          }}
          styleSlots={{
            antTableContainer: {
              overflow: 'hidden',
            },
            antTableHeader: {
              border: 'none !important',
              borderRadius: '0 !important',
            },
            tableCSS: {
              borderTopLeftRadius: !data?.data?.length ? '' : '0 !important',
              borderTopRightRadius: !data?.data?.length ? '' : '0 !important',
            },
            antTableBody: {
              paddingBottom: 10,
            },
          }}
        />
        <StyledPagination
          style={{
            alignSelf: 'center',
            marginTop: 28,
          }}
          showSizeChanger
          current={filter?.page || 1}
          total={data?.paginator?.totalCount || 1}
          pageSize={filter?.limit || 20}
          onChange={(page, pageSize) => {
            window.scrollTo(0, 0);
            handleRequest({ ...filter, page, limit: pageSize });
            setFilter({ ...filter, page, limit: pageSize });
            tableRef.current?.querySelector('.ant-table-body')?.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }}
          pageSizeOptions={['20', '50', '100']}
        />
      </StyledStack>
    </Layout>
  );
};

export default FilesManagement;
